import React from "react";
import { Box, Typography } from "@mui/material";

// Ratios réels des visuels fournis (Telemark_Laif_Ranglisten_Kopfzeile_2026.jpg
// et fusszeile.jpg) — 2244×512 et 2244×180 px. En pleine largeur de page,
// la hauteur du bandeau/footer est dérivée de ce ratio via CSS
// `aspect-ratio`, donc toujours exacte (ni recadrage, ni déformation),
// que la page soit en portrait (RankingTable) ou en paysage (PairingSheet).
// Surchargeable via `branding.bannerAspectRatio` / `branding.footerLogoAspectRatio`
// si d'autres visuels avec un ratio différent sont utilisés un jour.
const DEFAULT_BANNER_ASPECT_RATIO = 2244 / 512;
const DEFAULT_FOOTER_LOGO_ASPECT_RATIO = 2244 / 180;

// N'apparaît qu'à l'impression (display:none à l'écran) — le titre écran
// de la page (ex: "Bracket — Women1") reste géré séparément par la page.
//
// Le bandeau est en position fixe, collé aux bords de la feuille A4 (pas
// de marge, pas de bordure), et se répète automatiquement sur chaque page
// imprimée — comme sur les documents Vola Timing de référence. Pour que
// ça fonctionne, la page appelante doit définir `@page { margin: 0 }` et
// gérer elle-même ses marges internes via du padding sur son contenu (le
// header/footer étant positionnés par rapport à la feuille, pas à la
// zone de marge).
export function PrintHeader({ branding }) {
    if (
        !branding ||
        (!branding.raceTitle && !branding.logoLeft && !branding.logoRight && !branding.bannerImage)
    ) {
        return null;
    }

    const bannerAspectRatio = branding.bannerAspectRatio || DEFAULT_BANNER_ASPECT_RATIO;

    return (
        <Box
            sx={{
                display: "none",
                "@media print": {
                    display: "block",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    m: 0,
                },
            }}
        >
            {branding.bannerImage && (
                <Box
                    component="img"
                    src={branding.bannerImage}
                    alt=""
                    sx={{
                        width: "100%",
                        aspectRatio: String(bannerAspectRatio),
                        display: "block",
                        objectFit: "cover",
                    }}
                />
            )}

            {(branding.raceTitle || branding.logoLeft || branding.logoRight) && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                    }}
                >
                    {branding.logoLeft ? (
                        <Box
                            component="img"
                            src={branding.logoLeft}
                            alt=""
                            sx={{ maxHeight: 60, maxWidth: 140, objectFit: "contain" }}
                        />
                    ) : (
                        <Box sx={{ width: 140 }} />
                    )}

                    <Box sx={{ textAlign: "center", flexGrow: 1 }}>
                        {branding.raceTitle && (
                            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                {branding.raceTitle}
                            </Typography>
                        )}
                        {branding.subTitle && (
                            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                                {branding.subTitle}
                            </Typography>
                        )}
                    </Box>

                    {branding.logoRight ? (
                        <Box
                            component="img"
                            src={branding.logoRight}
                            alt=""
                            sx={{ maxHeight: 60, maxWidth: 140, objectFit: "contain" }}
                        />
                    ) : (
                        <Box sx={{ width: 140 }} />
                    )}
                </Box>
            )}
        </Box>
    );
}

// Réserve verticalement, sur CHAQUE page, la hauteur nécessaire au
// bandeau fixe — sans ça le contenu (première ligne du tableau) passe
// sous le header sur la 1ère page, et le navigateur ne laisse cet espace
// libre qu'en haut de la 1ère page lors du print puisque le header fixe
// n'occupe aucune place dans le flux normal du document.
//
// `sidePadding` doit correspondre exactement au padding horizontal
// appliqué par la page appelante à son conteneur de contenu (ex: "10mm"),
// pour que ce bloc, une fois ses marges négatives appliquées, ait
// exactement la même largeur que le bandeau fixe (position:fixed, qui
// lui ignore ce padding) — et donc, via le même aspect-ratio, exactement
// la même hauteur.
export function PrintHeaderSpacer({ branding, sidePadding = "10mm" }) {
    if (
        !branding ||
        (!branding.raceTitle && !branding.logoLeft && !branding.logoRight && !branding.bannerImage)
    ) {
        return null;
    }

    const bannerAspectRatio = branding.bannerAspectRatio || DEFAULT_BANNER_ASPECT_RATIO;
    const hasLogoRow = !!(branding.raceTitle || branding.logoLeft || branding.logoRight);

    return (
        <Box sx={{ display: "none", "@media print": { display: "block" } }}>
            {branding.bannerImage && (
                <Box
                    sx={{
                        width: `calc(100% + ${sidePadding} + ${sidePadding})`,
                        ml: `-${sidePadding}`,
                        mr: `-${sidePadding}`,
                        aspectRatio: String(bannerAspectRatio),
                    }}
                />
            )}
            {/* Espace approximatif pour la ligne logo/titre optionnelle
                sous le bandeau — pas d'aspect-ratio ici car c'est du
                texte/logos, pas une image à ratio fixe. */}
            {hasLogoRow && <Box sx={{ height: "20mm" }} />}
        </Box>
    );
}

export function PrintFooter({ branding }) {
    if (!branding || (!branding.footerText && !branding.footerLogo)) return null;

    const footerAspectRatio = branding.footerLogoAspectRatio || DEFAULT_FOOTER_LOGO_ASPECT_RATIO;

    return (
        <Box
            sx={{
                display: "none",
                "@media print": {
                    display: "block",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    m: 0,
                    textAlign: "center",
                },
            }}
        >
            {branding.footerLogo && (
                <Box
                    component="img"
                    src={branding.footerLogo}
                    alt=""
                    sx={{
                        width: "100%",
                        aspectRatio: String(footerAspectRatio),
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            )}
            {branding.footerText && (
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", py: 0.5 }}>
                    {branding.footerText}
                </Typography>
            )}
        </Box>
    );
}

// Réserve verticalement, sur CHAQUE page, la hauteur nécessaire au pied
// de page fixe — même logique que PrintHeaderSpacer, côté bas de page.
export function PrintFooterSpacer({ branding, sidePadding = "10mm" }) {
    if (!branding || (!branding.footerText && !branding.footerLogo)) return null;

    const footerAspectRatio = branding.footerLogoAspectRatio || DEFAULT_FOOTER_LOGO_ASPECT_RATIO;

    return (
        <Box sx={{ display: "none", "@media print": { display: "block" } }}>
            {branding.footerLogo && (
                <Box
                    sx={{
                        width: `calc(100% + ${sidePadding} + ${sidePadding})`,
                        ml: `-${sidePadding}`,
                        mr: `-${sidePadding}`,
                        aspectRatio: String(footerAspectRatio),
                    }}
                />
            )}
            {/* Espace approximatif pour la ligne de texte optionnelle
                sous le logo footer. */}
            {branding.footerText && <Box sx={{ height: "6mm" }} />}
        </Box>
    );
}